// -----------------------------------------------------------------------------
// DuplicateProductModal — Modal qui s'affiche quand un produit existe déjà
// StockPro / VisualisPro
//
// Affiché quand l'utilisateur tente d'ajouter un nouveau produit dont le nom
// existe déjà dans le stock (insensible à la casse, espaces ignorés).
//
// 3 options proposées :
//   1. Mettre à jour le produit existant (ajoute la nouvelle quantité au stock)
//   2. Modifier le nom (ferme ce modal et garde le formulaire ouvert)
//   3. Annuler (ferme tout)
// -----------------------------------------------------------------------------

import { getColors } from "../styles/theme";
import { Ic, ICONS } from "./Icons";

export default function DuplicateProductModal({
  dark,
  accent,
  existingProduct,  // le produit déjà en stock
  newQty,           // la quantité que l'utilisateur veut ajouter
  onUpdateExisting, // callback : "Mettre à jour le produit existant"
  onRename,         // callback : "Modifier le nom"
  onCancel,         // callback : "Annuler"
}) {
  const { textPrimary, textSecondary, borderColor } = getColors(dark);

  return (
    <div
      className="modal-bg"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      style={{ zIndex: 1000 }}
    >
      <div
        style={{
          background: dark ? "#0f0f1a" : "#fff",
          border: `1px solid ${borderColor}`,
          borderRadius: 18,
          padding: "24px",
          width: "100%",
          maxWidth: 460,
          boxShadow: "0 20px 60px rgba(0,0,0,.4)",
        }}
      >
        {/* Header avec icône d'avertissement */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `${accent}22`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 22 }}>⚠️</span>
          </div>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: textPrimary, margin: 0 }}>
              Produit déjà existant
            </h2>
            <p style={{ fontSize: 12, color: textSecondary, margin: "2px 0 0" }}>
              Un produit avec ce nom est déjà dans votre stock
            </p>
          </div>
        </div>

        {/* Carte d'infos du produit existant */}
        <div
          style={{
            background: dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 18,
            border: `1px solid ${borderColor}`,
          }}
        >
          <div style={{ fontSize: 11, color: textSecondary, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
            Produit existant
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>
            {existingProduct?.name}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12 }}>
            <span style={{ color: textSecondary }}>
              <strong style={{ color: textPrimary }}>{existingProduct?.qty}</strong> {existingProduct?.unit}
            </span>
            {existingProduct?.cat && (
              <span style={{ color: textSecondary }}>
                Catégorie : <strong style={{ color: textPrimary }}>{existingProduct.cat}</strong>
              </span>
            )}
            {existingProduct?.zone && (
              <span style={{ color: textSecondary }}>
                Zone : <strong style={{ color: textPrimary }}>{existingProduct.zone}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Question */}
        <div
          style={{
            fontSize: 13,
            color: textPrimary,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          Que voulez-vous faire avec la quantité <strong style={{ color: accent }}>+{newQty} {existingProduct?.unit}</strong> ?
        </div>

        {/* Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {/* Option 1 : Mettre à jour */}
          <button
            onClick={onUpdateExisting}
            className="glow-btn"
            style={{
              padding: "13px 16px",
              background: `linear-gradient(135deg, ${accent}, #f7931e)`,
              borderRadius: 11,
              color: "#fff",
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 18 }}>➕</span>
            <div>
              <div>Mettre à jour le produit existant</div>
              <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.9, marginTop: 2 }}>
                Le stock passera de {existingProduct?.qty} à {(parseFloat(existingProduct?.qty) || 0) + (parseFloat(newQty) || 0)} {existingProduct?.unit}
              </div>
            </div>
          </button>

          {/* Option 2 : Modifier le nom */}
          <button
            onClick={onRename}
            style={{
              padding: "13px 16px",
              background: dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
              borderRadius: 11,
              color: textPrimary,
              fontWeight: 600,
              border: `1px solid ${borderColor}`,
              cursor: "pointer",
              fontSize: 13,
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 18 }}>✏️</span>
            <div>
              <div>Modifier le nom du nouveau produit</div>
              <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.7, marginTop: 2 }}>
                Pour créer un produit différent
              </div>
            </div>
          </button>

          {/* Option 3 : Annuler */}
          <button
            onClick={onCancel}
            style={{
              padding: "11px 16px",
              background: "transparent",
              borderRadius: 11,
              color: textSecondary,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              fontSize: 12,
              marginTop: 4,
            }}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
